import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/pl';
import { Column } from "primereact/column";
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up.svg';
import { ReactComponent as TransactionsIcon } from '../../assets/images/transactions-widget-icon.svg';
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";
import { MessageContext } from '../../context/MessageContext';
import { useDashboardData } from '../../context/DashboardContext';
import { TransactionService } from '../../service/TransactionService';
import { Constants } from '../../utils/Constants';
import { FormattedUtils } from '../../utils/FormattedUtils';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import './TransactionsWidget.scss';
import './Widget.scss';

const TransactionsWidget = () => {
    return (
        <DataTableProvider>
            <TransactionsWidgetImpl />
        </DataTableProvider>
    );
};

const TransactionsWidgetToolbar = ({
    rowsPerPageOptions,
    defaultSearchCriteria,
    refreshTimer,
    isCollapsed,
    toggleCollapse,
}) => {
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);
    const { t } = useTranslation();

    return (
        <div className="flex align-items-center gap-2">
            {!isCollapsed && (
                <>
                    <span className="auto-refresh-timer">
                        {t('transactionsWidget.autoRefresh.updateIn')}: <span className="timer-number">{refreshTimer}</span> {t('transactionsWidget.autoRefresh.seconds')}
                    </span>

                    <Dropdown
                        className="small"
                        id="rowsPerPage"
                        options={rowsPerPageOptions}
                        value={searchCriteria?.rows ?? defaultSearchCriteria.rows}
                        onChange={e =>
                            setSearchCriteria(prev => ({
                                ...prev,
                                first: 0,
                                rows: e.value,
                                timestamp: Date.now(),
                            }))
                        }
                    />
                </>
            )}

            {isCollapsed ? (
                <ArrowUpIcon className="collapse-button" onClick={toggleCollapse} />
            ) : (
                <ArrowDownIcon className="collapse-button" onClick={toggleCollapse} />
            )}
        </div>
    );
};

const TransactionsWidgetImpl = () => {

    const { t, i18n } = useTranslation();
    const isMounted = useRef(false);
    const rowsPerPageOptions = [5, 10, 20, 50];
    const { showErrorMessage } = useContext(MessageContext);
    const { setSearchCriteria } = useContext(DataTableContext);
    const { locationOptions, deviceOptions, productOptions } = useDashboardData();
    const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [refreshTimer, setRefreshTimer] = useState(60);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 5,
        sortField: 'date',
        sortOrder: -1,
        baseQuery: '',
        filters: {}
    }), []);

    useEffect(() => {
        isMounted.current = true;
        fetchStatusOptions();
        fetchPaymentMethodOptions();
        return () => {
            isMounted.current = false;
        }
    }, []);

    
    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshTimer(prev => {
                if (prev <= 1) {
                    setSearchCriteria(prev => ({ ...prev, timestamp: Date.now() }));
                    return 60;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);


    const apiValueFromConverter = useMemo(() => 
        value => moment(value).startOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET), 
        []
    );
    const apiValueToConverter = useMemo(() => 
        value => moment(value).endOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET), 
        []
    );

    const filterConfig = useMemo(() => [        
        {
            fieldName: 'date',
            label: t('transactionsWidget.date'),
            type: FILTER_TYPES.DATE_RANGE,
            apiParamName: 'date',
            apiValueFromConverter,
            apiValueToConverter
        },
        {
            fieldName: 'location.name',
            label: t('transactionsWidget.locationName'),
            type: FILTER_TYPES.LIST,
            params: locationOptions,
            apiParamName: 'locations',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'device.name',
            label: t('transactionsWidget.deviceName'),
            type: FILTER_TYPES.LIST,
            params: deviceOptions,
            apiParamName: 'devices',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'product.productName',
            label: t('transactionsWidget.products'),
            type: FILTER_TYPES.LIST,    
            params: productOptions,
            apiParamName: 'products',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'totalGrossAmount',
            label: t('transactionsWidget.totalGrossAmount'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'total_gross_amount',
            params: {
                maxValue: 9999, // opcjonalne
                minValue: 0      // opcjonalne
            }
        },
        {
            fieldName: 'payment.paymentMethod',
            label: t('transactionsWidget.paymentMethod'),
            type: FILTER_TYPES.LIST,
            params: paymentMethodOptions,
            apiParamName: 'payment_methods',
            apiValueConverter:  value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'payment.cardNumber',
            label: t('transactionsWidget.cardNumber'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'card_number'
        },
        {
            fieldName: 'status',
            label: t('transactionsWidget.status'),
            type: FILTER_TYPES.LIST,
            params: statusOptions,
            apiParamName: 'statuses',
            apiValueConverter:  value => value.map(r => r.id).join(',')
        },
    ], [statusOptions, paymentMethodOptions, locationOptions, deviceOptions, productOptions]);

    const normalizeCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            return await TransactionService.getList(normalizeCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('transactionsWidget.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };

    const fetchStatusOptions = () => {
        TransactionService.getStatusList()
            .then(statuses => {
                if (isMounted.current) {
                    setStatusOptions(statuses.map(r => ({id: r, label: t(`TransactionStatus.${r}`)})))
                }
            })
            .catch(err => {
                showErrorMessage(t('transactionsWidget.error.fetchStatuses'))
            })
    }

    const fetchPaymentMethodOptions = () => {
        TransactionService.getPaymentMethodList()
            .then(paymentMethods => {
                if (isMounted.current) {
                    setPaymentMethodOptions(paymentMethods.map(r => ({ id: r, label: t(`PaymentMethod.${r}`) })))
                }
            })
            .catch(err => {
                showErrorMessage(t('transactionsWidget.error.fetchPaymentMethodOptions'))
            })
    }

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    }

    const tableToolbarLeft = (
        <div className='flex align-items-center gap-2'>
            <TransactionsIcon />
            <span className='widget-name'>{t('transactionsWidget.widgetName')}</span>
        </div>
    );

    const tableToolbarRight = (
        <TransactionsWidgetToolbar
            rowsPerPageOptions={rowsPerPageOptions}
            defaultSearchCriteria={defaultSearchCriteria}
            refreshTimer={refreshTimer}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
        />
    );

    return (
        <div className={`transactions-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <DataTable
                tableToolbarLeft={tableToolbarLeft}
                tableToolbarRight={tableToolbarRight}
                globalSearchPosition="none"
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                showRowsPerPage={false}
                showPaginator={false}
                minWidth="1100px"
            >
                <Column field="date" header={t('transactionsWidget.date')} sortable sortField="date" id="date"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatTimestamp(row.date)}
                    style={{ minWidth: '140px', maxWidth: '160px' }}
                />
                <Column field="locationName" header={t('transactionsWidget.locationName')} sortable sortField="location.name" id="locationName"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="deviceName" header={t('transactionsWidget.deviceName')} sortable sortField="device.name" id="deviceName"
                    style={{ minWidth: '130px', maxWidth: '170px' }}
                />
                <Column field="productName" header={t('transactionsWidget.products')} sortable sortField="product.productName" id="productName"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="totalGrossAmount" header={t('transactionsWidget.totalGrossAmount')} sortable id="totalGrossAmount"
                    className="price-column"
                    headerClassName="dt2-header-wrap"
                    body={row => FormattedUtils.formatPrice(row.totalGrossAmount, row.currency)}
                    style={{ minWidth: '130px', maxWidth: '150px' }}
                />
                <Column field="paymentMethod" header={t('transactionsWidget.paymentMethod')} sortable sortField="payment.paymentMethod" id="paymentMethod"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatEnum(row.paymentMethod, 'PaymentMethod')}
                    style={{ minWidth: '130px', maxWidth: '160px' }}
                />
                <Column field="cardNumber" header={t('transactionsWidget.cardNumber')} sortable sortField="payment.cardNumber" id="cardNumber"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="status" header={t('transactionsWidget.status')} sortable id="status"
                    body={row => RenderedHelper.formatEnum(row.status, 'TransactionStatus')}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
            </DataTable>
        </div>
    )

}

export default TransactionsWidget;